import React, { type ComponentType } from "react";
import { createBrowserRouter, isRouteErrorResponse, redirect, useRouteError } from "react-router-dom";
import RootLayout from "@/app/layout";
import HomePage from "@/app/page";
import { rpc } from "@/client/rpc";
import type { PublicProductPage } from "@/lib/productAggregates";
import { readPublicProductQuery } from "@/lib/productRoutes";

function lazyPage(loader: () => Promise<{ default: ComponentType }>) {
  return async () => ({ Component: (await loader()).default });
}

function RouteError() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const message = isRouteErrorResponse(error)
    ? error.statusText || error.data
    : error instanceof Error
      ? error.message
      : "The requested page could not be loaded.";
  return (
    <main className="layout-container flex min-h-[55vh] items-center justify-center py-16">
      <section className="max-w-xl border border-border bg-card p-8 text-center">
        <p className="text-[10px] uppercase tracking-[0.24em] text-primary">
          {notFound ? "404" : "Application error"}
        </p>
        <h1 className="mt-3 text-2xl font-light uppercase tracking-widest">
          {notFound ? "Page not found" : "Unable to load page"}
        </h1>
        <p className="mt-4 text-sm text-foreground/65">{String(message)}</p>
        <a className="mt-6 inline-block text-xs underline underline-offset-4" href="/">Return home</a>
      </section>
    </main>
  );
}

export function createAppRouter() {
  return createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, Component: HomePage },
      { path: "admin", lazy: lazyPage(() => import("@/app/admin/page")), loader: () => rpc("getAllProducts") },
      { path: "admin/campaigns", lazy: lazyPage(() => import("@/app/admin/campaigns/page")) },
      { path: "admin/coupons", lazy: lazyPage(() => import("@/app/admin/coupons/page")) },
      { path: "admin/inquiries", lazy: lazyPage(() => import("@/app/admin/inquiries/page")) },
      {
        path: "admin/shipping-rates",
        lazy: lazyPage(() => import("@/app/admin/shipping-rates/page")),
        loader: () => rpc("getAdminShippingScheme"),
      },
      {
        path: "admin/site-settings",
        lazy: lazyPage(() => import("@/app/admin/site-settings/page")),
        loader: () => rpc("getSiteSettings"),
      },
      { path: "auth/callback", lazy: lazyPage(() => import("@/app/auth/callback/page")) },
      { path: "brands", lazy: lazyPage(() => import("@/app/brands/page")) },
      {
        path: "brands/:slug",
        lazy: lazyPage(() => import("@/app/brands/[slug]/page")),
        loader: () => rpc("getCatalogProducts"),
      },
      { path: "cart", lazy: lazyPage(() => import("@/app/cart/page")) },
      { path: "catalog", lazy: lazyPage(() => import("@/app/catalog/page")), loader: () => rpc("getCatalogProducts") },
      { path: "catalog-old", loader: () => redirect("/catalog") },
      { path: "checkout", lazy: lazyPage(() => import("@/app/checkout/page")) },
      { path: "checkout/complete", lazy: lazyPage(() => import("@/app/checkout/complete/page")) },
      { path: "contact", lazy: lazyPage(() => import("@/app/contact/page")) },
      { path: "faq", lazy: lazyPage(() => import("@/app/faq/page")), loader: () => rpc("getStaticPage", ["faq"]) },
      { path: "flow", lazy: lazyPage(() => import("@/app/flow/page")) },
      { path: "guidance", lazy: lazyPage(() => import("@/app/guidance/page")) },
      {
        path: "info/*",
        lazy: lazyPage(() => import("@/app/info/[...slug]/page")),
        loader: ({ params }) => rpc("getStaticPage", [params["*"] || ""]),
      },
      { path: "login", lazy: lazyPage(() => import("@/app/login/page")) },
      { path: "mail", lazy: lazyPage(() => import("@/app/mail/page")) },
      { path: "mail/mobile", lazy: lazyPage(() => import("@/app/mail/mobile/page")) },
      {
        path: "product",
        lazy: lazyPage(() => import("@/app/product/[id]/page")),
        loader: ({ request }) => {
          const query = readPublicProductQuery(new URL(request.url));
          if (!query) {
            throw new Response("Add a sku, name, or product_id query.", { status: 400 });
          }
          return rpc("getProductQueryPageBundle", [query.field, query.term]);
        },
      },
      {
        path: "product/:sku",
        lazy: lazyPage(() => import("@/app/product/[id]/page")),
        loader: async ({ params }) => {
          const requestedSku = params.sku || "";
          const bundle = await rpc<{ pageProduct: PublicProductPage | null } & Record<string, unknown>>(
            "getProductPageBundle",
            [requestedSku],
          );
          const canonicalSku = bundle.pageProduct?.product.sku;
          if (canonicalSku && canonicalSku !== requestedSku) {
            return redirect(`/product/${encodeURIComponent(canonicalSku)}`);
          }
          return bundle;
        },
      },
      { path: "profile", lazy: lazyPage(() => import("@/app/profile/page")) },
      { path: "signup", lazy: lazyPage(() => import("@/app/signup/page")) },
      {
        path: "unsubscribe",
        lazy: lazyPage(() => import("@/app/unsubscribe/page")),
        loader: ({ request }) => {
          const url = new URL(request.url);
          return rpc("unsubscribe", [
            url.searchParams.get("email") || "",
            url.searchParams.get("token") || "",
            url.searchParams.get("status") || "",
          ]);
        },
      },
      { path: "*", loader: () => { throw new Response("Not found", { status: 404 }); } },
    ],
  },
  ]);
}
